/* eslint-disable max-lines-per-function, max-len, no-console, jsx-a11y/no-autofocus */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Loader2, Eye, EyeOff, CheckCircle, AlertCircle, Key } from 'lucide-react';
import logoEmblem from '@/assets/logos/vehix-emblem.png';

export const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExchanging, setIsExchanging] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle PKCE code exchange and session detection
  // IMPORTANT: Check for existing session FIRST before trying to exchange code
  // because detectSessionInUrl: true may have already processed the PKCE code
  useEffect(() => {
    const handleSessionExchange = async () => {
      setIsExchanging(true);
      setError(null);

      try {
        // FIRST: Check if we already have a session
        // With detectSessionInUrl: true, Supabase automatically processes PKCE codes
        // on page load, which consumes the one-time code. If that happened, we'll have
        // a valid session already, so check for it BEFORE trying to exchange the code.
        const {
          data: { session: existingSession }
        } = await supabase.auth.getSession();

        if (existingSession) {
          console.log(
            '[ResetPasswordPage] Session already exists (likely from detectSessionInUrl auto-processing)'
          );
          setHasValidSession(true);
          setIsExchanging(false);
          // Clean up URL if there are any auth params
          const code = searchParams.get('code');
          if (code || window.location.hash) {
            window.history.replaceState({}, '', window.location.pathname);
          }
          return;
        }

        // Check for PKCE code in query params
        const code = searchParams.get('code');
        const type = searchParams.get('type');

        // If we have a code (PKCE flow), exchange it for a session
        if (code) {
          console.log('[ResetPasswordPage] Exchanging PKCE code for session...');
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('[ResetPasswordPage] Code exchange error:', exchangeError);
            // Don't show error immediately - the code might have been consumed by detectSessionInUrl
            // Check one more time for a session that might have been established
            const {
              data: { session: retrySession }
            } = await supabase.auth.getSession();
            if (retrySession) {
              console.log('[ResetPasswordPage] Session found on retry after code exchange error');
              setHasValidSession(true);
              setIsExchanging(false);
              window.history.replaceState({}, '', window.location.pathname);
              return;
            }
            setError(
              'This reset link is invalid or has expired. Please request a new password reset email.'
            );
            setIsExchanging(false);
            return;
          }

          if (data?.session) {
            console.log('[ResetPasswordPage] Session established from code exchange');
            setHasValidSession(true);
            setIsExchanging(false);
            // Clean up URL - remove code and type params
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        }

        // Check for hash tokens (fallback for non-PKCE flows)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');

        if (accessToken && (hashType === 'recovery' || type === 'recovery')) {
          console.log('[ResetPasswordPage] Setting session from hash tokens...');
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });

          if (sessionError) {
            console.error('[ResetPasswordPage] Session error:', sessionError);
            setError(
              'This reset link is invalid or has expired. Please request a new password reset email.'
            );
            setIsExchanging(false);
            return;
          }

          if (sessionData?.session) {
            console.log('[ResetPasswordPage] Session established from hash tokens');
            setHasValidSession(true);
            setIsExchanging(false);
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
        }

        // No session found from any method
        setError(
          'This reset link is invalid or has expired. Please request a new password reset email.'
        );
        setIsExchanging(false);
      } catch (err) {
        console.error('[ResetPasswordPage] Error during session exchange:', err);
        setError(
          'An error occurred while processing your reset link. Please request a new password reset email.'
        );
        setIsExchanging(false);
      }
    };

    handleSessionExchange();
  }, [searchParams]);

  // Redirect after successful password reset
  useEffect(() => {
    if (success) {
      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate('/auth', { replace: true });
      }, 2000);
    }
  }, [success, navigate]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);

      // Update user password using Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Sign out the user after password reset (security best practice)
      // They'll need to sign in again with the new password
      await supabase.auth.signOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while exchanging code/session
  if (isExchanging) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-card rounded-lg shadow-lg p-8 text-center border border-border'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-56 h-56' />
          </div>
          <Loader2 className='h-12 w-12 text-primary mx-auto mb-4 animate-spin' />
          <h1 className='text-2xl font-bold text-foreground mb-2'>Verifying Reset Link</h1>
          <p className='text-muted-foreground'>
            Please wait while we verify your password reset link...
          </p>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-card rounded-lg shadow-lg p-8 text-center border border-border'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-56 h-56' />
          </div>
          <CheckCircle className='h-16 w-16 text-green-500 dark:text-green-400 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-foreground mb-2'>Password Reset Successful!</h1>
          <p className='text-muted-foreground mb-4'>
            Your password has been reset. Redirecting you to the login page...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if no valid session
  if (!hasValidSession) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center p-4'>
        <div className='w-full max-w-md bg-card rounded-lg shadow-lg p-8 text-center border border-border'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-56 h-56' />
          </div>
          <AlertCircle className='h-16 w-16 text-red-500 dark:text-red-400 mx-auto mb-4' />
          <h1 className='text-2xl font-bold text-foreground mb-2'>Invalid Reset Link</h1>
          <p className='text-muted-foreground mb-4'>
            {error || 'This link is invalid or has expired.'}
          </p>
          <button
            onClick={() => navigate('/auth')}
            className='bg-primary text-primary-foreground py-2 px-4 rounded-md hover:opacity-90 transition-colors font-medium'
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Show password reset form - use card surface so input text is readable in light and dark
  const inputBase =
    'w-full px-3 py-2 pr-10 border border-input rounded-md ' +
    'bg-card text-card-foreground placeholder:text-muted-foreground ' +
    'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ' +
    'disabled:opacity-50 disabled:cursor-not-allowed ' +
    'dark:bg-card dark:text-card-foreground dark:placeholder:text-muted-foreground dark:border-input';

  return (
    <div className='min-h-screen bg-background flex items-center justify-center p-4'>
      <div className='w-full max-w-md bg-card rounded-lg shadow-lg p-8 border border-border'>
        <div className='text-center mb-6'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-56 h-56' />
          </div>
          <div className='flex items-center justify-center mb-2'>
            <Key className='h-6 w-6 text-primary mr-2' />
            <h1 className='text-2xl font-bold text-foreground'>Reset Your Password</h1>
          </div>
          <p className='text-muted-foreground'>Please enter your new password below.</p>
        </div>

        {error && (
          <div className='mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md flex items-center gap-2'>
            <AlertCircle className='h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0' />
            <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='password' className='block text-sm font-medium text-foreground mb-2'>
              New Password
            </label>
            <div className='relative'>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Enter your new password'
                className={inputBase}
                required
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-3 top-2.5 text-muted-foreground hover:text-foreground focus:outline-none'
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>
              Must be at least 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          <div>
            <label
              htmlFor='confirmPassword'
              className='block text-sm font-medium text-foreground mb-2'
            >
              Confirm New Password
            </label>
            <div className='relative'>
              <input
                id='confirmPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm your new password'
                className={inputBase}
                required
                disabled={isSubmitting}
              />
              <button
                type='button'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className='absolute right-3 top-2.5 text-muted-foreground hover:text-foreground focus:outline-none'
                disabled={isSubmitting}
              >
                {showConfirmPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
              </button>
            </div>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !password || !confirmPassword}
            className='w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:opacity-90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className='mt-6 text-center'>
          <button
            onClick={() => navigate('/auth')}
            className='text-sm text-primary hover:opacity-90 font-semibold'
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};
